"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.LicenseQuantityList = void 0;
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));

const isEqual_1 = __importDefault(require("lodash/isEqual"));

const isString_1 = __importDefault(require("lodash/isString"));

const sumBy_1 = __importDefault(require("lodash/sumBy"));

const toNumber_1 = __importDefault(require("lodash/toNumber"));

const types_1 = require("./types");

class LicenseQuantityList {
  /**
   * @description Creates a new LicenseQuantityList for use.
   *
   * @param {Array} items License Quantities which make up this list
   */
  constructor(items = []) {
    this.items = [];
    this.savedState = [];
    this.items = parse(items);
    this.registerSavedState();
  }
  /** Public methods **/

  /**
   * @description Method to determine the quantity which may be assigned.
   * @returns {Number}  quantity that are assignable
   */


  getAssignableQuantity() {
    // The paid, pending, grace past due, or cancelling statuses are considered assignable.
    // The past-due status is not.
    return this.sumWhen(licenseQuantity => licenseQuantity.status === types_1.LICENSE_QUANTITY_STATUS.GRACE_PAST_DUE || licenseQuantity.status === types_1.LICENSE_QUANTITY_STATUS.PAID || licenseQuantity.status === types_1.LICENSE_QUANTITY_STATUS.PENDING_PAYMENT || licenseQuantity.status === types_1.LICENSE_QUANTITY_STATUS.CANCELLING);
  }
  /**
   * @description Method to transform the list into the smallest representation.
   *   This helps reduce the amount of traffic our server has to deal with, in
   *   addition to altering models to conform to server/API expectations (in
   *   many cases).
   *
   * @param {Boolean} [filterToModified] - Whether to limit to quantities
   *    that have been modified. Defaults to false.
   * @returns {Array} minimum necessary representation of the list
   */


  toMinimumModel(filterToModified = false) {
    return !filterToModified || this.hasUnsavedChanges() ? this.items.map(item => {
      if (item.quantity === types_1.LICENSE_QUANTITY_UNLIMITED) {
        return {
          quantity: types_1.LICENSE_QUANTITY_UNLIMITED
        };
      }

      return {
        quantity: item.quantity
      };
    }) : [];
  }
  /**
   * Checks if a product's assigned licenses is set to unlimited.
   * @returns {Boolean} true if quantity is set to 'UNLIMITED'
   */


  hasUnlimitedLicenses() {
    return this.items.some(licenseQuantity => licenseQuantity.isUnlimited);
  }
  /**
   * @description Method to determine if there are any unsaved changes to this list.
   *
   * @returns {Boolean} true if there are unsaved changes, else false
   */


  hasUnsavedChanges() {
    // we re-parse as some editing elements may change the quantity back to a string
    return !isEqual_1.default(parse(this.items), this.savedState);
  }
  /** Helper Methods **/

  /**
   * @description Sums the item quantities, filtered by the provided condition.
   *
   * @param {Function} condition the filter to apply
   * @returns {Number} the sum of the filtered quantities
   */


  sumWhen(condition) {
    if (this.items && this.items.length > 0) {
      return sumBy_1.default(this.items, licenseQuantity => {
        return !Number.isNaN(licenseQuantity.quantity) && condition(licenseQuantity) ? licenseQuantity.quantity : 0;
      });
    }

    return 0;
  }
  /**
   * @description Updates model with a nested form of itself recording state
   *     which may be later modified.
   */


  registerSavedState() {
    this.savedState = cloneDeep_1.default(parse(this.items));
  }

}

exports.LicenseQuantityList = LicenseQuantityList;

function parse(licenseQuantities) {
  // UNLIMITED is currently given as a string from JIL, so we convert to the number.NaN
  const clonedQuantities = cloneDeep_1.default(licenseQuantities);
  return clonedQuantities.map(licenseQuantity => {
    if (isString_1.default(licenseQuantity.quantity)) {
      return Object.assign(Object.assign({}, licenseQuantity), {
        isUnlimited: licenseQuantity.quantity === types_1.LICENSE_QUANTITY_UNLIMITED,
        quantity: toNumber_1.default(licenseQuantity.quantity)
      });
    }

    return Object.assign(Object.assign({}, licenseQuantity), {
      isUnlimited: false
    });
  });
}

exports.default = LicenseQuantityList;